import { CommonModule } from '@angular/common';
import {
	Component,
	QueryList,
	ViewChildren,
	Input,
	computed,
	inject,
	effect,
	signal,
	AfterViewInit,
	OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { ISessionEntry } from '../timing-tower/IRaceTiming';
import { VbLxpCheckboxComponent } from '../controls/checkbox/vb-lxp-checkbox.component';
import { VbLxpSwitchComponent } from '../controls/switch/vb-lxp-switch.component';
import { TimespanPipe } from '../shared/time-span.pipe';
import { TimingTowerComponent } from '../timing-tower/timing-tower.component';
import { VbLxpPanelComponent } from '../panel/vb-lxp-panel.component';
import { VbLxpVideoSlotComponent } from '../video/video-slot/video-slot.component';
import { WindowSizeService } from '../shared/window-size.service';
import { VideoLayout, VideoLayoutService } from '../shared/video-layout.service';
import { FeatureSettingsService } from '../shared/feature-settings.service';
import { PanelService } from '../panel/panel.service';
import { StreamData } from '../shared/stream.token';
import { RaceSessionService } from '../timing-tower/race-session.service';
import { RaceClassComponent } from '../shared/race-class.component';
import { ENABLE_TWITCH_STREAM_SELECTOR } from '../shared/livestream-options.token';

import { LivestreamViewService } from './livestream-view.service';
import { TwitchChatComponent } from '../twitch-chat/twitch-chat.component';
import { WebSocketService } from '../shared/websocket.service';

const LAYOUT_CLASS_MAP: Record<VideoLayout, string> = {
	[VideoLayout.SINGLE]: 'aspect-16/9',
	[VideoLayout.SIDE_BY_SIDE]: 'aspect-32/9',
	[VideoLayout.LARGE_LEFT]: 'aspect-48/18',
	[VideoLayout.LARGE_RIGHT]: 'aspect-48/18',
	[VideoLayout.GRID_2X2]: 'aspect-16/9',
	[VideoLayout.STACKED]: 'aspect-16/18',
};

function toFiniteNumber(value: any): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === 'string') {
		const parsed = Number.parseFloat(value);
		return Number.isFinite(parsed) ? parsed : null;
	}

	return null;
}

function formatFahrenheit(value: number): string {
	return `${Math.round(value)}°F`;
}

function isPlausibleTrackTempF(value: number): boolean {
	return value >= 20 && value <= 180;
}

function directionFromDegrees(degrees: number): string {
	const normalized = ((degrees % 360) + 360) % 360;
	const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
	const index = Math.round(normalized / 45) % points.length;
	return points[index];
}

@Component({
	selector: 'app-livestream-view',
	imports: [
		CommonModule,
		TimespanPipe,
		RouterModule,
		VbLxpCheckboxComponent,
		VbLxpPanelComponent,
		VbLxpSwitchComponent,
		VbLxpVideoSlotComponent,
		TwitchChatComponent,
		TimingTowerComponent,
		RaceClassComponent,
	],
	templateUrl: './livestream-view.component.html',
	styleUrl: './livestream-view.component.css',
	host: {
		'[style.height]': `'100%'`,
	},
})
export class LivestreamViewComponent implements AfterViewInit, OnDestroy {
	@Input() public streamChatTitle = 'SRO Stream Chat';
	@Input() public enableStreamChatPopout = true;
	@Input() public streamChatPopoutUrl =
		`https://www.twitch.tv/popout/sromotorsports/chat?popout=&parent=${window.location.hostname}`;

	@ViewChildren('livestreamPlayerSlot')
	currentLivestreamPlayerSlots: QueryList<VbLxpVideoSlotComponent>;

	public layoutService = inject(VideoLayoutService);
	public raceSessionService = inject(RaceSessionService);

	private windowSizeService = inject(WindowSizeService);
	private panelService = inject(PanelService);
	private featureSettingsService = inject(FeatureSettingsService);
	public showTwitchStreamSelector =
		inject(ENABLE_TWITCH_STREAM_SELECTOR, { optional: true }) ?? false;
	public livestreamViewService = inject(LivestreamViewService);
	public webSocketService = inject(WebSocketService);

	public VideoLayout = VideoLayout;
	public isMobile = toSignal(this.windowSizeService.isMobile$);
	public moreColumns = signal(false);
	public isMainStreamSelected = signal(true);
	public isSessionArchived = signal(false);
	public prevIsSessionArchived = signal(false);
	public raceStatus = toSignal(this.raceSessionService.raceStatus$);
	public timeStatus = toSignal(this.raceSessionService.timeStatus$);
	public weather = toSignal(this.webSocketService.weather$, { initialValue: {} as any });
	public featureSettings = toSignal(this.featureSettingsService.featureSettings$);
	public selectedClasses = toSignal(this.raceSessionService.selectedClasses$, { initialValue: [] });
	public localNow = signal(new Date());
	public trackTempDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const trackTempValue =
			currentData?.trackTemperatureF ??
			currentData?.trackTempF ??
			currentData?.trackTemperature ??
			currentData?.trackTemp ??
			null;
		const ambientTempValue =
			currentData?.ambientTemperatureF ??
			currentData?.airTemperatureF ??
			currentData?.ambientTempF ??
			currentData?.airTempF ??
			null;

		const trackTemp = toFiniteNumber(trackTempValue);
		const ambientTemp = toFiniteNumber(ambientTempValue);

		if (trackTemp !== null && isPlausibleTrackTempF(trackTemp)) {
			return formatFahrenheit(trackTemp);
		}

		if (ambientTemp !== null) {
			return formatFahrenheit(ambientTemp);
		}

		if (trackTempValue !== null) {
			const tempString = String(trackTempValue);
			return tempString.includes('°') ? tempString : `${tempString}°F`;
		}

		return '--';
	});
	public windSpeedDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;

		const mph = toFiniteNumber(
			currentData?.windSpeedMph ??
				currentData?.windMph ??
				currentData?.windSpeedMPH ??
				currentData?.windSpeed ??
				null,
		);
		if (mph !== null) {
			return `${Math.round(mph)} mph`;
		}

		const kph = toFiniteNumber(currentData?.windSpeedKph ?? currentData?.windKph ?? null);
		if (kph !== null) {
			return `${Math.round(kph * 0.621371)} mph`;
		}

		const mps = toFiniteNumber(
			currentData?.windSpeedMps ?? currentData?.windMps ?? currentData?.windSpeedMS ?? null,
		);
		if (mps !== null) {
			return `${Math.round(mps * 2.23694)} mph`;
		}

		return '--';
	});
	public windDirectionDegrees = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const degrees = toFiniteNumber(
			currentData?.windDirectionDegrees ??
				currentData?.windDirectionDeg ??
				currentData?.windDirection ??
				currentData?.windDirDegrees ??
				null,
		);

		if (degrees === null) {
			return 0;
		}

		return ((degrees % 360) + 360) % 360;
	});
	public windDirectionLabel = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const cardinals =
			currentData?.windDirectionCardinal ??
			currentData?.windDirectionText ??
			currentData?.windDirectionCompass ??
			null;

		if (cardinals !== null) {
			return String(cardinals);
		}

		const degrees = toFiniteNumber(
			currentData?.windDirectionDegrees ??
				currentData?.windDirectionDeg ??
				currentData?.windDirection ??
				currentData?.windDirDegrees ??
				null,
		);
		if (degrees === null) {
			return '--';
		}

		return directionFromDegrees(degrees);
	});
	public localTimeDisplay = computed(() => {
		const weather = this.weather();
		const currentData = weather?.currentData ?? weather;
		const streamLocalTime =
			currentData?.localTime ?? currentData?.localTimestamp ?? currentData?.sessionLocalTime ?? null;

		if (streamLocalTime !== null) {
			const timeString = String(streamLocalTime);
			const parsed = new Date(timeString);
			if (!Number.isNaN(parsed.getTime())) {
				return parsed.toLocaleTimeString([], {
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: true,
				});
			}
			return timeString;
		}

		return this.localNow().toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		});
	});

	public hidePlayerMobile = computed(() => {
		return this.isMobile() && this.livestreamViewService.isNoPlayerSelected();
	});

	protected containerAspectRatioClass = '';
	private localClockInterval: ReturnType<typeof setInterval>;

	constructor() {
		this.localClockInterval = setInterval(() => {
			this.localNow.set(new Date());
		}, 1000);

		this.layoutService.activeLayout$.subscribe((activeLayout: VideoLayout) => {
			this.containerAspectRatioClass = LAYOUT_CLASS_MAP[activeLayout];
			setTimeout(() => {
				const currentPlayers = this.currentLivestreamPlayerSlots
					.toArray()
					.map((lps) => lps.mvPlayer);
				this.livestreamViewService.updateCurrentPlayers(currentPlayers);
			});
		});

		this.raceSessionService.isSessionArchived$
			.pipe(takeUntilDestroyed())
			.subscribe((isArchived) => {
				this.prevIsSessionArchived.set(this.isSessionArchived());
				this.isSessionArchived.set(isArchived);
				if (isArchived && !this.layoutService.isLayout(VideoLayout.SINGLE)) {
					this.layoutService.setLayout(VideoLayout.SINGLE);
				}
				if (isArchived && this.panelService.isPanelOpen('timing')) {
					this.panelService.closePanel('timing');
				}
				// Open timing tower when session switches from archived to not archived
				if (this.prevIsSessionArchived() && !this.isSessionArchived()) {
					this.panelService.openPanel('timing');
				}
			});

		effect(() => {
			if (this.moreColumns()) {
				this.panelService.closePanel('chat');
			}
		});

		effect(() => {
			const chatOpen = this.chatPanelOpen();
			if (chatOpen && this.moreColumns()) {
				this.panelService.closePanel('timing');
				setTimeout(() => this.panelService.openPanel('chat'));
			}
		});

		effect(() => {
			const timingOpen = this.timingPanelOpen();
			if (timingOpen && this.moreColumns()) {
				this.panelService.closePanel('chat');
				setTimeout(() => this.panelService.openPanel('timing'));
			}
		});
	}

	private chatPanelOpen = toSignal(this.panelService.getPanelState('chat'));
	private timingPanelOpen = toSignal(this.panelService.getPanelState('timing'));

	ngAfterViewInit(): void {
		// SSE connection is now managed centrally in app.ts

		const currentPlayers = this.currentLivestreamPlayerSlots.toArray().map((lps) => lps.mvPlayer);
		this.livestreamViewService.updateCurrentPlayers(currentPlayers);
		if (this.isSessionArchived() && this.panelService.isPanelOpen('timing')) {
			this.panelService.closePanel('timing');
		}
	}

	ngOnDestroy(): void {
		clearInterval(this.localClockInterval);
	}

	public openFullTimingPage() {
		const currentQueryParams = window.location.search;
		const newRoute = '/full-timing';
		const fullUrl = `${newRoute}${currentQueryParams}`;

		window.open(fullUrl, '_blank');
	}

	public openFullMapPage() {
		const currentParams = new URLSearchParams(window.location.search).toString();
		const base = new URL('map-popout', document.baseURI).toString();
		const url = currentParams ? `${base}?${currentParams}` : base;
		const width = 1280;
		const height = 720;
		window.open(url, '_blank', `width=${width},height=${height},resizable=yes`);
	}

	public openStreamChatPopout() {
		if (!this.streamChatPopoutUrl) {
			return;
		}

		const width = 420;
		const height = 760;
		const left = Math.max((window.screen.width - width) / 2, 0);
		const top = Math.max((window.screen.height - height) / 2, 0);

		window.open(
			this.streamChatPopoutUrl,
			'stream-chat-popout',
			`popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
		);
	}

	public onTimingTowerSelectionAdded(team: ISessionEntry) {
		this.livestreamViewService.addSlotItem({
			type: 'timing',
			id: team.number,
			streamData: this.livestreamViewService.getStreamDataByCarNumber(team.number),
		});
	}

	public onTimingTowerSelectionRemoved(team: ISessionEntry) {
		this.livestreamViewService.removeSlotItem({
			type: 'timing',
			id: team.number,
			streamData: this.livestreamViewService.getStreamDataByCarNumber(team.number),
		});
	}

	public onTwitchStreamToggled(isSelected: boolean) {
		if (isSelected) {
			this.livestreamViewService.addSlotItem({ type: 'twitchStream' });
			this.livestreamViewService.isTwitchStreamSelected.set(true);
		} else {
			this.livestreamViewService.removeSlotItem({ type: 'twitchStream' });
			this.livestreamViewService.isTwitchStreamSelected.set(false);
		}
	}

	public onPlayerClosed(streamData: StreamData) {
		if (streamData.carNumber) {
			this.livestreamViewService.removeSlotItem({
				type: 'timing',
				id: streamData.carNumber,
				streamData,
			});
		} else if (streamData.id === 'main-stream') {
			this.livestreamViewService.removeSlotItem({
				type: 'mainStream',
			});
		} else if (streamData.id === 'twitch-stream') {
			this.livestreamViewService.removeSlotItem({
				type: 'twitchStream',
			});
		}
	}

	onClassSelectionChange(selectedClasses: string[]) {
		this.raceSessionService.updateSelectedClasses(selectedClasses);
	}
}
